import { useEffect } from "react";

/**
 * Warns before leaving a page with unsaved changes: a native "beforeunload"
 * prompt for closing/reloading the tab, and a router-level block for in-app
 * navigation away from it.
 * @param {boolean} hasUnsavedChanges Whether there are changes to warn about.
 * @param {boolean} isActive Whether the guard should be active at all (e.g. only while editing an existing record).
 * @param {object} history The router history object to block navigation on.
 */
export function useUnsavedChangesGuard(hasUnsavedChanges, isActive, history) {
    useEffect(() => {
        if (!hasUnsavedChanges || !isActive) {
            return undefined;
        }

        const handleBeforeUnload = (event) => {
            event.preventDefault();
            event.returnValue = "";
        };
        window.addEventListener("beforeunload", handleBeforeUnload);
        return () => window.removeEventListener("beforeunload", handleBeforeUnload);
    }, [hasUnsavedChanges, isActive]);

    useEffect(() => {
        if (!hasUnsavedChanges || !isActive) {
            return undefined;
        }

        /*
         * Returning a message (not calling window.confirm ourselves) lets history's own
         * confirmation flow show/resolve the native prompt — the documented, reliable way
         * to block a transition, rather than racing our own confirm against the router's.
         */
        const unblock = history.block(
            "You have unsaved changes. Leave this page and discard them?"
        );
        return unblock;
    }, [hasUnsavedChanges, history, isActive]);
}
